import { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// "Suggest a correction" for a Fact (target fact) or a Live issue (target live, with the section picked from `sections`).
export const CorrectionDialog = ({ target, id, sections = [], testid = "correction", triggerClass = "" }) => {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState(sections[0]?.[0] || "");
  const [claim, setClaim] = useState("");
  const [url, setUrl] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/corrections", { target, id, section, claim, source_url: url }); setSent(true); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reset = (o) => { setOpen(o); if (!o) { setSent(false); setClaim(""); setUrl(""); } };
  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>
        <button type="button" className={`inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm transition-colors hover:border-slate-400 ${triggerClass}`} data-testid={`${testid}-open-btn`}><Flag size={14} />Suggest a correction</button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle>Suggest a correction</DialogTitle>
          <DialogDescription>Middle re-runs its fact check against approved sources and verified Facts. If they confirm your correction, it's fixed straight away and counts towards your contributions.</DialogDescription></DialogHeader>
        {sent ? (
          <div className="space-y-2 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900" data-testid={`${testid}-result`}>
            <p className="font-semibold" data-testid={`${testid}-thanks`}>Thank you, we'll re-run our fact check based on your feedback.</p>
            <p className="text-xs text-emerald-800">If approved sources confirm it, Middle updates it and it's added to the history. Either way, we'll send you a notification.</p>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => reset(false)} data-testid={`${testid}-done`}>Done</Button>
          </div>
        ) : (<>
          {target === "live" && <label className="block text-xs text-slate-500">Which part is wrong?
            <Select value={section} onValueChange={setSection}><SelectTrigger className="mt-1 text-slate-900" data-testid={`${testid}-section`}><SelectValue /></SelectTrigger>
              <SelectContent>{sections.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`${testid}-section-${k}`}>{l}</SelectItem>)}</SelectContent></Select></label>}
          <label className="block text-xs text-slate-500">What's wrong, and what's right?
            <Textarea value={claim} onChange={(e) => setClaim(e.target.value)} rows={4} maxLength={1000} placeholder="e.g. Unemployment is 4.3%, not 4.1%: the ABS released new figures on 13 November." className="mt-1 text-slate-900" data-testid={`${testid}-claim`} /></label>
          <label className="block text-xs text-slate-500">Link to an approved source (recommended)
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="mt-1 text-slate-900" data-testid={`${testid}-source`} /></label>
          <p className="text-[11px] text-slate-500">Your word alone isn't evidence: links from sources outside Middle's approved list aren't used. You can send 10 corrections a day.</p>
          <Button className="rounded-full" disabled={busy || claim.trim().length < 15 || (target === "live" && !section)} onClick={submit} data-testid={`${testid}-submit`}>Send for checking</Button>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
